<script setup>
/**
 * SearchCardBatchesMenu — "In N batches ⌄" for a global-search result.
 *
 * Shown instead of a single "View in Batch" when the user owns the searched
 * database card in more than one batch. Opens the BELONGS TO BATCHES list;
 * picking a row opens that batch at that copy of the card.
 *
 * The count and the rows come from the search response itself — the inventory
 * hits grouped by `batch_id` (see `helpers/globalSearch`) — so this costs no
 * extra request per row.
 */
import { computed, ref } from 'vue'
import AppButton from '@/components/atoms/AppButton'
import { AppMenuPanel, AppMenuPanelSection } from '@/components/molecules/AppMenuPanel'

const props = defineProps({
  /** `[{ id, name, addedLabel, cardId }]` from `batchesForHits`. */
  batches: { type: Array, required: true },
  /**
   * Total batches for the card. Defaults to what's listed, but the API's own
   * count (once it sends one) is authoritative — the listed rows only cover the
   * copies that came back on this page.
   */
  count: { type: Number, default: 0 },
})

const emit = defineEmits(['select'])

const open = ref(false)

const total = computed(() => props.count || props.batches.length)

const select = (batch) => {
  open.value = false
  emit('select', batch)
}
</script>

<template>
  <!-- `portal`: the row lives inside a Combobox inside a Dialog, a stacking
       context the panel would otherwise be trapped under. -->
  <AppMenuPanel
    v-model:open="open"
    placement="bottom-end"
    width="w-[280px]"
    portal
    @click.stop
  >
    <template #trigger>
      <!-- The toggle lives on a NATIVE span, not on AppButton.
           `AppButton` declares `click` in its emits, so `@click.stop` on it is a
           component-event listener rather than a DOM one — the native click kept
           bubbling to the ComboboxOption, which selected the row.
           `mousedown.prevent` is the other half: HeadlessUI's Combobox closes on
           blur, and closing unmounts this option — taking the teleported panel
           with it before the click could land. Preventing mousedown keeps focus
           in the search input, so the panel survives long enough to be used. -->
      <span
        class="inline-flex"
        @mousedown.stop.prevent
        @click.stop.prevent="open = !open"
      >
        <AppButton variant="outlined" size="sm" :dropdown="true" :open="open">
          In {{ total }} batches
        </AppButton>
      </span>
    </template>

    <AppMenuPanelSection label="Belongs to batches" :divider="false">
      <button
        v-for="batch in props.batches"
        :key="batch.id"
        type="button"
        class="flex flex-col w-full items-start px-4 py-2.5 text-left transition-colors hover:bg-gray-50"
        @mousedown.stop.prevent
        @click.stop.prevent="select(batch)"
      >
        <span class="text-sm font-medium text-gray-900 truncate w-full">{{ batch.name }}</span>
        <span v-if="batch.addedLabel" class="text-xs text-gray-500">{{ batch.addedLabel }}</span>
      </button>
    </AppMenuPanelSection>
  </AppMenuPanel>
</template>
